/**
 * The typed API client (§31, §33, §55).
 *
 * One place that knows how to talk to the backend, so the rules live here
 * rather than being re-implemented per feature:
 *
 *   - unwrap the §16 envelope: `{ data, meta }` on success, `{ error }` on
 *     failure, and throw a typed ApiError either way
 *   - carry an `X-Request-ID` so a child's report can be traced through the
 *     backend logs (§70: "Request IDs traceable")
 *   - attach `Idempotency-Key` to mutations that can be double-tapped (§55)
 *   - refresh the access token once on 401, never in a loop (§13)
 *   - time out, because a hung fetch on a bad connection is indistinguishable
 *     from a frozen app to a seven-year-old
 */

import { ApiError, toApiError, type ApiErrorPayload } from './errors';

export interface Envelope<T> {
  data: T;
  meta: { request_id: string };
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: Method;
  body?: unknown;
  /** §55: makes a retried mutation safe. Generated per user intent, not per attempt. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  /** Skips the Authorization header — used only by the pre-auth endpoints. */
  anonymous?: boolean;
}

/**
 * Why a refresh did not produce a new access token.
 *
 * Declared here rather than in the auth feature so that the client does not
 * have to import from it -- the bridge below exists precisely to keep that
 * dependency pointing one way.
 *
 * The distinction is the whole point: `rejected` is the server refusing the
 * refresh token, which ends the session, and `unreachable` is not having
 * reached the server at all, which must not. Collapsing the two into a
 * boolean is what signed children out on a dropped request.
 */
export type RefreshOutcome = 'ok' | 'rejected' | 'unreachable';

/** Everything the client needs from the auth layer, without importing it. */
export interface AuthBridge {
  getAccessToken(): string | null;
  refresh(): Promise<RefreshOutcome>;
  onAuthFailure(): void;
}

const DEFAULT_TIMEOUT_MS = 20_000;
/** AI work legitimately takes longer; §46 targets p95 under 15s for explanations. */
export const AI_TIMEOUT_MS = 45_000;

/**
 * The 401s that mean "this session is over, get a new access token".
 *
 * A 401 carrying anything else is about the request, not the session.
 *
 * `TOKEN_INVALID` is here and `INVALID_CREDENTIALS` is deliberately not.
 * The backend used to answer both "your access token has a bad signature"
 * and "that parent PIN was wrong" with INVALID_CREDENTIALS, which left no
 * safe reading: treat it as fatal and a parent is signed out for mistyping
 * four digits; ignore it and a child whose token is unusable sits on a
 * screen of failed requests, still `authenticated` as far as the guards are
 * concerned, with no way back to sign in. The backend now separates them.
 */
const SESSION_EXPIRY_CODES = new Set<string>([
  'AUTHENTICATION_REQUIRED',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'SESSION_REVOKED',
]);

let authBridge: AuthBridge | null = null;

export function setAuthBridge(bridge: AuthBridge): void {
  authBridge = bridge;
}

export function newRequestId(): string {
  return crypto.randomUUID();
}

/** A stable key for one user intent, so retries of it collapse (§55). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

function baseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function errorFromPayload(payload: unknown, status: number): ApiError {
  const candidate = (payload as { error?: ApiErrorPayload } | null)?.error;
  if (candidate && typeof candidate.code === 'string') {
    return new ApiError({
      code: candidate.code as ApiError['code'],
      status,
      message: candidate.message,
      details: candidate.details ?? {},
      retryable: candidate.retryable ?? false,
      requestId: candidate.request_id,
    });
  }
  // A non-enveloped failure means something in front of the app answered — a
  // proxy, a gateway, an HTML error page. Not the backend's contract.
  return new ApiError({
    code: status === 401 ? 'AUTHENTICATION_REQUIRED' : 'INTERNAL_ERROR',
    status,
    retryable: status >= 500,
  });
}

async function performRequest(
  path: string,
  options: RequestOptions,
  requestId: string,
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-ID': requestId,
  };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  if (!options.anonymous) {
    const token = authBridge?.getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  // Combine the caller's signal with our timeout, so either can abort.
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const signal = options.signal
    ? AbortSignal.any([options.signal, timeout.signal])
    : timeout.signal;

  try {
    return await fetch(`${baseUrl()}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? null : JSON.stringify(options.body),
      signal,
      credentials: 'same-origin',
    });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Make a request and return the unwrapped `data`.
 *
 * On 401 it refreshes **once** and replays. §13: "Handle 401 without infinite
 * refresh loops" — the `retried` flag is that guarantee, and a second 401
 * signs the child out rather than trying again.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const requestId = newRequestId();

  const send = async (): Promise<Response> => {
    try {
      return await performRequest(path, options, requestId);
    } catch (cause) {
      throw toApiError(cause);
    }
  };

  let response = await send();

  if (response.status === 401 && !options.anonymous && authBridge) {
    // Not every 401 is "your session ended". The parent PIN is a second
    // factor checked on an already-authenticated route, so a wrong PIN also
    // answers 401 — and refreshing, replaying and then signing the user out
    // would turn a typo into a sign-out. Only the session codes get the
    // refresh-once treatment; anything else is the caller's error to handle.
    // The body can only be read once, so parse it here and reuse it on every
    // branch below rather than reaching for `response` again.
    const failure = errorFromPayload(await parseBody(response), 401);
    if (!SESSION_EXPIRY_CODES.has(failure.code)) throw failure;

    const outcome = await authBridge.refresh();
    if (outcome === 'rejected') {
      authBridge.onAuthFailure();
      throw failure;
    }
    if (outcome === 'unreachable') {
      // The session was not refused -- we never got an answer. Signing the
      // child out here is how a tunnel used to log them out of a thirty-day
      // session. Report it as what it is and let the screen offer a retry.
      throw new ApiError({
        code: typeof navigator !== 'undefined' && navigator.onLine === false ? 'OFFLINE' : 'NETWORK_ERROR',
        status: 0,
        retryable: true,
      });
    }

    response = await send();
    if (response.status === 401) {
      authBridge.onAuthFailure();
      throw errorFromPayload(await parseBody(response), 401);
    }
  }

  const payload = await parseBody(response);

  if (!response.ok) {
    throw errorFromPayload(payload, response.status);
  }

  if (response.status === 204 || payload === null) {
    return undefined as T;
  }

  const envelope = payload as Partial<Envelope<T>>;
  if (!envelope || typeof envelope !== 'object' || !('data' in envelope)) {
    // The backend always envelopes. Anything else means we are talking to
    // something that is not our API — fail loudly rather than render garbage.
    throw new ApiError({
      code: 'MALFORMED_RESPONSE',
      status: response.status,
      requestId,
    });
  }

  return envelope.data as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  // `del` rather than `delete`, which is a reserved word.
  del: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
